import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { dataPoolApi } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DatabaseZap, ArrowRight, BrainCircuit, CheckCircle2, AlertTriangle, FileText, Upload } from "lucide-react";

export default function PoolWidget() {
  const navigate = useNavigate();
  const [pool, setPool] = useState(null);
  const [latestUpload, setLatestUpload] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [poolRes, uploadsRes] = await Promise.all([
          dataPoolApi.get(),
          dataPoolApi.getUploads(),
        ]);
        setPool(poolRes.data);
        const uploads = uploadsRes.data || [];
        if (uploads.length > 0) setLatestUpload(uploads[0]);
      } catch (e) { console.error(e); }
    };
    load();
  }, []);

  if (!pool) return null;

  const items = pool.items || [];
  const pooled = items.filter(i => i.status === "pooled").length;
  const review = items.filter(i => i.status === "review_required").length;
  const analysed = items.filter(i => i.status === "analysed" && !i.approved).length;
  const approvedPending = items.filter(i => i.approved && !i.committed).length;
  const pending = items.filter(i => !i.committed).length;

  // Determine the primary status and CTA
  let statusBadge = null;
  let primaryAction = null;

  if (pooled > 0) {
    statusBadge = <Badge className="text-[9px] bg-cyan-500/15 text-cyan-400 border-none">Ready to Study</Badge>;
    primaryAction = { label: `Study ${pooled} Items`, onClick: () => navigate("/dashboard/data-pool") };
  } else if (review > 0 || analysed > 0) {
    statusBadge = <Badge className="text-[9px] bg-amber-500/15 text-amber-400 border-none">Needs Review</Badge>;
    primaryAction = { label: `Review ${review + analysed} Items`, onClick: () => navigate("/dashboard/data-pool") };
  } else if (approvedPending > 0) {
    statusBadge = <Badge className="text-[9px] bg-emerald-500/15 text-emerald-400 border-none">Ready to Commit</Badge>;
    primaryAction = { label: `Commit ${approvedPending} Items`, onClick: () => navigate("/dashboard/data-pool") };
  }

  // Empty state
  if (pending === 0) {
    return (
      <Card className="bg-slate-900/40 border-slate-800/50 border-dashed" data-testid="pool-widget-empty">
        <CardContent className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-slate-800/60 flex items-center justify-center">
              <DatabaseZap className="w-4 h-4 text-slate-600" />
            </div>
            <div>
              <span className="text-xs text-slate-500">No imported data waiting for review</span>
              <span className="block text-[10px] text-slate-600 mt-0.5">Upload financial files to begin</span>
            </div>
          </div>
          <Button size="sm" variant="outline" className="h-7 text-[10px] border-slate-700 gap-1" onClick={() => navigate("/dashboard/ingest")} data-testid="pool-widget-import-btn">
            <Upload className="w-3 h-3" /> Import Data
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-slate-900/40 border-slate-800/50 border-l-[3px] border-l-cyan-500/60" data-testid="pool-widget">
      <CardHeader className="pb-2 pt-3 px-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <DatabaseZap className="w-4 h-4 text-cyan-400" />
            <CardTitle className="text-xs text-slate-500 uppercase tracking-[0.15em] font-manrope font-medium">Data Pool</CardTitle>
            {statusBadge}
          </div>
          <button onClick={() => navigate("/dashboard/data-pool")} className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1 transition-colors" data-testid="pool-widget-open-btn">
            Open Pool <ArrowRight className="w-3 h-3" />
          </button>
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-3">
        <div className="flex items-center gap-4 mb-3">
          {/* Counts */}
          <div className="flex gap-3">
            {pooled > 0 && (
              <div className="text-center" data-testid="pool-widget-pooled">
                <div className="text-lg font-mono tabular text-slate-300">{pooled}</div>
                <div className="text-[9px] text-slate-600 uppercase">Pooled</div>
              </div>
            )}
            {review > 0 && (
              <div className="text-center" data-testid="pool-widget-review">
                <div className="text-lg font-mono tabular text-amber-400">{review}</div>
                <div className="text-[9px] text-amber-500/60 uppercase">Review</div>
              </div>
            )}
            {approvedPending > 0 && (
              <div className="text-center" data-testid="pool-widget-approved">
                <div className="text-lg font-mono tabular text-emerald-400">{approvedPending}</div>
                <div className="text-[9px] text-emerald-500/60 uppercase">Approved</div>
              </div>
            )}
            <div className="text-center">
              <div className="text-lg font-mono tabular text-slate-50">{pending}</div>
              <div className="text-[9px] text-slate-600 uppercase">Pending</div>
            </div>
          </div>

          {/* Latest upload info */}
          {latestUpload && (
            <div className="flex-1 min-w-0 pl-3 border-l border-slate-800/50">
              <div className="flex items-center gap-1.5">
                <FileText className="w-3 h-3 text-slate-600 shrink-0" />
                <span className="text-[10px] text-slate-500 truncate">{latestUpload.source_file || "Recent upload"}</span>
              </div>
              <div className="flex gap-1.5 mt-1">
                {latestUpload.pooled > 0 && <Badge className="text-[7px] bg-slate-500/15 text-slate-400 border-none">{latestUpload.pooled} pooled</Badge>}
                {latestUpload.review > 0 && <Badge className="text-[7px] bg-amber-500/15 text-amber-400 border-none">{latestUpload.review} review</Badge>}
                {latestUpload.approved > 0 && <Badge className="text-[7px] bg-emerald-500/15 text-emerald-400 border-none">{latestUpload.approved} approved</Badge>}
                {latestUpload.committed > 0 && <Badge className="text-[7px] bg-cyan-500/15 text-cyan-400 border-none">{latestUpload.committed} committed</Badge>}
              </div>
            </div>
          )}
        </div>

        {/* CTA */}
        {primaryAction && (
          <Button size="sm" className="w-full h-7 text-[10px] bg-cyan-600 hover:bg-cyan-500 gap-1.5" onClick={primaryAction.onClick} data-testid="pool-widget-cta">
            <BrainCircuit className="w-3 h-3" /> {primaryAction.label}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
